import {
  AfterViewInit,
  Component,
  Input,
  OnInit,
  ViewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { tap } from 'rxjs/operators';
import { AppService } from 'src/app/app.service';
import { UtilsService } from 'src/app/shared/utils.service';
import { SitesSelectorComponent } from 'src/app/shared/sites-selector/sites-selector.component';
import { ApiDataSource } from '../../../utils/api-data-source';
import { ApiRequestService } from '../../../shared/api-request.service';
import { MatSort, Sort } from '@angular/material/sort';
import { MatPaginator } from '@angular/material/paginator';
import { RBACService } from '../../../shared/rbac.service';

@Component({
  selector: 'app-employees-notifications-sites',
  templateUrl: './employees-notifications-sites.component.html',
  styleUrls: ['./employees-notifications-sites.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class EmployeesNotificationsSitesComponent
  implements OnInit, AfterViewInit {
  // Get the user id.
  @Input('user_id') user_id: number;

  // Columns to show and the data source.
  displayedColumns: string[] = [
    'select',
    'name',
    'site_type',
    'site_status',
    'site_risk_assessment',
    'actions'
  ];
  dataSource: EmployeesNotificationNotificationsSitesDataSource = new EmployeesNotificationNotificationsSitesDataSource(
    this.app,
    this.api,
    true
  );

  // The paginator and sorter.
  @ViewChild(MatPaginator, { static: false }) paginator: MatPaginator;
  @ViewChild(MatSort, { static: false }) sort: MatSort;

  // RBAC permission flags: resolved once in ngOnInit().
  canView: boolean = false;
  canEdit: boolean = false;

  constructor(
    public route: ActivatedRoute,
    public router: Router,
    public app: AppService,
    private api: ApiRequestService,
    public utils: UtilsService,
    public rbac: RBACService
  ) {}

  // Expose RBACService to template.
  protected readonly RBACService = RBACService;

  ngOnInit(): void {
    // 1. Resolve RBAC permission flags.
    this.canView = this.rbac.userCanAccess(
      [
        RBACService.getPermission(
          RBACService.RBAC_F.F_SITES,
          RBACService.RBAC_A.A_VIEW
        )
      ],
      true
    );
    this.canEdit = this.rbac.userCanAccess(
      [
        RBACService.getPermission(
          RBACService.RBAC_F.F_ACCOUNT_NOTIFICATION_ROLES,
          RBACService.RBAC_A.A_EDIT
        )
      ],
      true
    );

    // Store the user id before making API requests.
    this.dataSource.user_id = this.user_id;

    // 2. Build displayedColumns dynamically.
    this.displayedColumns = [
      'name',
      'site_type',
      'site_status',
      'site_risk_assessment',
      'actions'
    ];
    if (this.canEdit) {
      this.displayedColumns.unshift('select');
    }

    // 3. Only load data if user has view permission (sites:view).
    if (this.canView) {
      this.dataSource.getData(true);
    }
  }

  ngAfterViewInit(): void {
    // The table, its matSort and its mat-paginator all live inside a container gated by
    // *ngIf="canView", so without that permission Angular never creates those view
    // children and this.sort / this.paginator stay undefined. Bail out before wiring them up.
    if (!this.canView) {
      return;
    }

    // Reset the paginator when sorting.
    this.sort.sortChange.subscribe(
      (): void => {
        this.paginator.pageIndex = 0;
      }
    );

    const _tap: any = tap(
      (): void => {
        this.dataSource.limit = this.paginator.pageSize;
        this.dataSource.offset = this.paginator.pageIndex;
        this.dataSource.order_by = this.sort.active;
        this.dataSource.order = this.sort.direction;

        // Refresh the data.
        this.dataSource.getData();
      }
    );

    // Subscribe to the paginator tap events.
    this.paginator.page.pipe(_tap).subscribe();

    // Subscribe to the sorter tap events.
    this.sort.sortChange
      .pipe(
        tap(
          (sorter: Sort): void => {
            this.dataSource.order = sorter['direction'];
            this.dataSource.getData(true);
          }
        )
      )
      .subscribe();
  }

  /**
   * Opens the site selector dialog and syncs selected sites with the user.
   *
   * @returns {void}
   */
  onSelectSites(): void {
    if (
      !this.rbac.userCanAccess(
        [
          RBACService.getPermission(
            RBACService.RBAC_F.F_ACCOUNT_NOTIFICATION_ROLES,
            RBACService.RBAC_A.A_EDIT
          )
        ],
        true
      )
    ) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to manage employee notification sites.'
      );
      return;
    }

    // Show the site selector.
    this.utils.showComponentDialog(
      SitesSelectorComponent,
      {
        multiple: true,
        selected: this.dataSource.allIds
      },
      {
        width: '1200px'
      },
      (site_ids: number[]): void => {
        if (typeof site_ids !== 'undefined') {
          // Make an update request to the API.
          this.api
            .makeRequest(
              'put',
              `v2/employee/${this.user_id}/notifications/sites`,
              {
                site_ids: site_ids
              }
            )
            .then(
              (): void => {
                this.utils.showToast(
                  'The selected ' +
                    this.utils
                      .getLangTerm(
                        'parent-child-sites-combined.plural',
                        'Sites'
                      )
                      .toLowerCase() +
                    ' were synced successfully to the user.'
                );
              }
            )
            .finally(
              (): void => {
                this.dataSource.getData(true);
              }
            );
        }
      }
    );
  }

  /**
   * Links all sites to the notification role.
   * Shows a confirmation modal before making a request to link all sites to the user.
   * Upon successful update, a success toast is displayed.
   * Finally, reloads the data source.
   *
   * @return {void}
   */
  onLinkAllSites(): void {
    if (
      !this.rbac.userCanAccess(
        [
          RBACService.getPermission(
            RBACService.RBAC_F.F_ACCOUNT_NOTIFICATION_ROLES,
            RBACService.RBAC_A.A_EDIT
          )
        ],
        true
      )
    ) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to manage employee notification sites.'
      );
      return;
    }

    // Make a request to link all sites to the notification role.
    this.utils.showModal(
      'Link All Sites',
      `Are you sure you want to link all ${this.utils
        .getLangTerm('parent-child-sites-combined.plural', 'Sites')
        .toLowerCase()} to the user?`,
      (): void => {
        // Make an update request to the API.
        this.api
          .makeRequest(
            'put',
            `v2/employee/${this.user_id}/notifications/sites`,
            {
              site_ids: 'all'
            }
          )
          .then(
            (): void => {
              this.utils.showToast(
                'All ' +
                  this.utils
                    .getLangTerm('parent-child-sites-combined.plural', 'Sites')
                    .toLowerCase() +
                  ' were synced successfully to the user.'
              );
            }
          )
          .finally(
            (): void => {
              this.dataSource.getData(true);
            }
          );
      }
    );
  }

  /**
   * Unlinks a site from the user.
   *
   * @param {number} site_id - The ID of the site to unlink.
   *
   * @param evt
   * @return {void}
   */
  onUnlinkSite(site_id: number, evt: any): void {
    if (
      !this.rbac.userCanAccess(
        [
          RBACService.getPermission(
            RBACService.RBAC_F.F_ACCOUNT_NOTIFICATION_ROLES,
            RBACService.RBAC_A.A_EDIT
          )
        ],
        true
      )
    ) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to manage employee notification sites.'
      );
      return;
    }

    this.utils.showQuickActions(
      evt.target,
      'Are you sure you want to unlink the ' +
        this.utils
          .getLangTerm('parent-child-sites-combined.singular', 'Site')
          .toLowerCase() +
        ' from the user?',
      [
        {
          text: 'Yes',
          handler: (): void => {
            // Unlink the site.
            this.unlinkSites([site_id]);
          }
        },
        {
          text: 'No',
          handler: (): void => {}
        }
      ]
    );
  }

  /**
   * Unlinks selected sites from the user.
   *
   * @return {void}
   */
  onUnlinkSelectedSites(): void {
    if (
      !this.rbac.userCanAccess(
        [
          RBACService.getPermission(
            RBACService.RBAC_F.F_ACCOUNT_NOTIFICATION_ROLES,
            RBACService.RBAC_A.A_EDIT
          )
        ],
        true
      )
    ) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to manage employee notification sites.'
      );
      return;
    }

    // Get the selected site ids.
    const ids: any[] = this.dataSource.selection.selected;
    // Check if we have sites selected.
    if (ids.length == 0) {
      this.utils.showModal(
        'Unlink Sites',
        'Please select some ' +
          this.utils
            .getLangTerm('parent-child-sites-combined.plural', 'Sites')
            .toLowerCase() +
          ' to unlink from the user.'
      );
      return;
    }
    // Make a request to delete the notification roles.
    this.utils.showModal(
      'Unlink Sites',
      `Are you sure you want to unlink (${ids.length}) ${this.utils
        .getLangTerm('parent-child-sites-combined.plural', 'Sites')
        .toLowerCase()} from the user?`,
      (): void => {
        this.unlinkSites(this.dataSource.selection.selected).then(
          (): void => {
            // Clear the list selection.
            this.dataSource.selection.clear();
          }
        );
      }
    );
  }

  /**
   * Method to unlink all sites from the user.
   *
   * @return {void}
   */
  onUnlinkAllSites(): void {
    if (
      !this.rbac.userCanAccess(
        [
          RBACService.getPermission(
            RBACService.RBAC_F.F_ACCOUNT_NOTIFICATION_ROLES,
            RBACService.RBAC_A.A_EDIT
          )
        ],
        true
      )
    ) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to manage employee notification sites.'
      );
      return;
    }

    // Make a request to delete the notification roles.
    this.utils.showModal(
      'Unlink All Sites',
      `Are you sure you want to unlink all ${this.utils
        .getLangTerm('parent-child-sites-combined.plural', 'Sites')
        .toLowerCase()} from the user?`,
      (): void => {
        this.unlinkSites('all').then(
          (): void => {
            // Clear the list selection.
            this.dataSource.selection.clear();
          }
        );
      }
    );
  }

  /**
   * This method unlinks the specified sites from the user.
   *
   * @param {number[]} site_ids - An array of site IDs to unlink from the user.
   * @returns {Promise<any>} - A promise that resolves when the unlinking is complete.
   * @private
   */
  private unlinkSites(site_ids: number[] | string): Promise<any> {
    // Make an API request to unlink sites.
    return this.api
      .makeRequest(
        'delete',
        `v2/employee/${this.user_id}/notifications/sites`,
        {},
        {
          site_ids: typeof site_ids == 'string' ? site_ids : site_ids.join(',')
        }
      )
      .then(
        (): void => {
          this.utils.showToast(
            'The ' +
              this.utils
                .getLangTerm('parent-child-sites-combined.plural', 'Sites')
                .toLowerCase() +
              ' were detached from the user.'
          );
          this.dataSource.getData();
        }
      );
  }
}

export class EmployeesNotificationNotificationsSitesDataSource extends ApiDataSource {
  // Order and order by params.
  order_by: string = 'name';
  order: string = 'asc';

  // Store the user id.
  user_id: number;

  getData(resetOffset: boolean = false): void {
    this.makeRequest(
      `v2/employee/${this.user_id}/notifications/sites`,
      resetOffset,
      {
        order_by: this.order_by,
        order: this.order
      }
    );
  }
}
